import { lazy, type ReactNode } from 'react'
import type { RouteObject } from 'react-router'

import { RequirePermission } from './guards'

const ProfilePage = lazy(() => import('@/features/settings/profile-page'))
const SettingsHubPage = lazy(() => import('@/features/settings/settings-hub'))
const OrganizationPage = lazy(() => import('@/features/settings/organization-page'))
const LocationsPage = lazy(() => import('@/features/locations/locations-page'))
const LocationDetailPage = lazy(() => import('@/features/locations/location-detail-page'))
const TeamPage = lazy(() => import('@/features/team/team-page'))
const CatalogPage = lazy(() => import('@/features/catalog/catalog-page'))
const ProvidersPage = lazy(() => import('@/features/providers/providers-page'))
const ProviderDetailPage = lazy(() => import('@/features/providers/provider-detail-page'))
const TemplatesPage = lazy(() => import('@/features/templates/templates-page'))
const TemplateDetailPage = lazy(() => import('@/features/templates/template-detail-page'))
const RulesPage = lazy(() => import('@/features/rules/rules-page'))
const BrandingPage = lazy(() => import('@/features/branding/branding-page'))
const IntegrationsPage = lazy(() => import('@/features/integrations/integrations-page'))
const BillingPage = lazy(() => import('@/features/billing/billing-page'))
const AuditLogPage = lazy(() => import('@/features/audit/audit-log-page'))

/**
 * Everything under `/settings`, split out of router.tsx to keep both files inside the 300-line
 * budget. Each route is gated by the permission that reveals its hub card (FRONTEND_PLAN §8 #3).
 */
export function settingsRoutes(lazyElement: (node: ReactNode) => ReactNode): RouteObject[] {
  return [
    { path: '/settings', element: lazyElement(<SettingsHubPage />) },
    { path: '/settings/profile', element: lazyElement(<ProfilePage />) },
    {
      path: '/settings/organization',
      element: lazyElement(
        <RequirePermission requires={['organizations:read']}>
          <OrganizationPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/locations',
      element: lazyElement(
        <RequirePermission requires={['locations:read']}>
          <LocationsPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/locations/:id',
      element: lazyElement(
        <RequirePermission requires={['locations:read']}>
          <LocationDetailPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/team',
      element: lazyElement(
        <RequirePermission requires={['users:read']}>
          <TeamPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/templates',
      element: lazyElement(
        <RequirePermission requires={['message-templates:read']}>
          <TemplatesPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/templates/:id',
      element: lazyElement(
        <RequirePermission requires={['message-templates:read']}>
          <TemplateDetailPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/rules',
      element: lazyElement(
        <RequirePermission requires={['workflow-rules:read']}>
          <RulesPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/branding',
      element: lazyElement(
        <RequirePermission requires={['branding:read']}>
          <BrandingPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/integrations',
      element: lazyElement(
        <RequirePermission requires={['integrations:read']}>
          <IntegrationsPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/billing',
      element: lazyElement(
        <RequirePermission requires={['billing:read']}>
          <BillingPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/audit-log',
      element: lazyElement(
        <RequirePermission requires={['audit-log:read']}>
          <AuditLogPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/services',
      element: lazyElement(
        <RequirePermission requires={[]}>
          <CatalogPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/providers',
      element: lazyElement(
        <RequirePermission requires={['providers:read']}>
          <ProvidersPage />
        </RequirePermission>,
      ),
    },
    {
      path: '/settings/providers/:id',
      element: lazyElement(
        <RequirePermission requires={['providers:read']}>
          <ProviderDetailPage />
        </RequirePermission>,
      ),
    },
  ]
}
